import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { API_URL } from "../../config";

const PERIODS = [[7, "7 дней"], [30, "30 дней"], [90, "90 дней"]];
const WEEKDAYS = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"];
const DEVICES = { mobile: "Телефон", tablet: "Планшет", desktop: "Компьютер" };
const KIND_PATH = { articles: "articles", lectures: "lectures", cases: "cases" };

export function fmtSeconds(s) {
  if (!s) return "—";
  const m = Math.floor(s / 60);
  return m ? `${m} мин ${String(s % 60).padStart(2, "0")} с` : `${s} с`;
}

const pct = v => (v === null || v === undefined ? "—" : `${v}%`);

function Card({ label, value, hint }) {
  return (
    <div className="stats-card" title={hint}>
      <div className="stats-card-value">{value}</div>
      <div className="stats-card-label">{label}</div>
    </div>
  );
}

function Bars({ data, label, height = 90 }) {
  const max = Math.max(1, ...data.map(d => d.value));
  return (
    <div className="stats-bars" style={{ height }} role="img" aria-label={label}>
      {data.map((d, i) => (
        <div key={i} className="stats-bar-col" title={`${d.title}: ${d.value}`}>
          <div className="stats-bar" style={{ height: `${Math.max(d.value ? 3 : 0, (d.value / max) * 100)}%` }} />
        </div>
      ))}
    </div>
  );
}

function List({ rows, total }) {
  if (!rows.length) return <p className="stats-empty">Пока нет данных.</p>;
  return (
    <ul className="stats-list">
      {rows.map(r => (
        <li key={r.name}>
          <span>{r.name}</span>
          <span className="stats-list-bar"><i style={{ width: `${total ? (r.value * 100) / total : 0}%` }} /></span>
          <b>{r.value}</b>
        </li>
      ))}
    </ul>
  );
}

export default function StatsTab({ token }) {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    setData(null); setError("");
    fetch(`${API_URL}/admin/analytics?days=${days}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => (r.ok ? r.json() : Promise.reject(r.status)))
      .then(setData)
      .catch(() => setError("Не удалось загрузить статистику"));
  }, [days, token]);

  if (error) return <p className="stats-empty">{error}</p>;
  if (!data) return <p className="stats-empty">Загрузка…</p>;
  const t = data.totals;
  const sourcesTotal = data.sources.reduce((a, s) => a + s.sessions, 0);
  const devicesTotal = Object.values(data.devices).reduce((a, n) => a + n, 0);

  return (
    <div className="stats-tab">
      <div className="stats-head">
        <h3 className="admin-section-title" style={{ margin: 0 }}>Статистика посещений</h3>
        <div className="stats-periods" role="tablist" aria-label="Период">
          {PERIODS.map(([d, label]) => (
            <button key={d} type="button" role="tab" aria-selected={days === d} className={days === d ? "on" : ""} onClick={() => setDays(d)}>{label}</button>
          ))}
        </div>
      </div>
      <p className="stats-note">
        Данные собирает сам портал, без имён и IP-адресов; боты не считаются. Время — московское.
        Просмотр видео внутри плеера ВКонтакте не измеряется: видно только время на странице.
      </p>

      <div className="stats-cards">
        <Card label="Посетителей" value={t.visitors} hint="Уникальные браузеры за период" />
        <Card label="Визитов" value={t.sessions} hint="Серии открытий без пауз дольше 30 минут" />
        <Card label="Просмотров страниц" value={t.hits} />
        <Card label="Сейчас на сайте" value={t.online_now} hint="Были активны в последние 5 минут" />
        <Card label="Новых" value={t.new_visitors} hint="Впервые у нас за этот период" />
        <Card label="Вернувшихся" value={t.returning_visitors} hint="Были раньше начала периода или пришли повторно" />
        <Card label="Вернулись за 7 дней" value={pct(t.came_back_7d_pct)} hint={`Из ${t.came_back_cohort} посетителей, у которых прошла неделя после первого визита`} />
        <Card label="Среднее время визита" value={fmtSeconds(t.avg_visit_seconds)} hint="Активное время, когда страница открыта и пользователь что-то делал" />
        <Card label="Страниц за визит" value={t.pages_per_visit} />
        <Card label="Ушли с одной страницы" value={pct(t.single_page_visits_pct)} />
        <Card label="Дочитали статью" value={pct(t.read_to_end_pct)} hint="Статьи и случаи: прокрутили не меньше 75% страницы" />
      </div>

      <h4 className="stats-h">Посетители по дням</h4>
      <Bars label="Посетители по дням" height={110}
        data={data.by_day.map(d => ({ value: d.visitors, title: d.date }))} />
      <div className="stats-axis"><span>{data.by_day[0]?.date}</span><span>{data.by_day[data.by_day.length - 1]?.date}</span></div>

      <div className="stats-two">
        <div>
          <h4 className="stats-h">В какие часы заходят</h4>
          <Bars label="Просмотры по часам" data={data.by_hour.map((v, h) => ({ value: v, title: `${h}:00` }))} />
          <div className="stats-axis"><span>0:00</span><span>12:00</span><span>23:00</span></div>
        </div>
        <div>
          <h4 className="stats-h">В какие дни недели</h4>
          <Bars label="Просмотры по дням недели" data={data.by_weekday.map((v, i) => ({ value: v, title: WEEKDAYS[i] }))} />
          <div className="stats-axis">{WEEKDAYS.map(d => <span key={d}>{d}</span>)}</div>
        </div>
      </div>

      <div className="stats-two">
        <div>
          <h4 className="stats-h">Откуда приходят (визиты)</h4>
          <List total={sourcesTotal} rows={data.sources.map(s => ({ name: s.name, value: s.sessions }))} />
        </div>
        <div>
          <h4 className="stats-h">Устройства (визиты)</h4>
          <List total={devicesTotal} rows={Object.entries(data.devices).map(([k, v]) => ({ name: DEVICES[k] || k, value: v }))} />
        </div>
      </div>

      <h4 className="stats-h">Что читают и смотрят</h4>
      {data.top.length === 0 ? <p className="stats-empty">Пока нет данных.</p> : (
        <div className="stats-table-wrap">
          <table className="stats-table">
            <thead>
              <tr><th>Материал</th><th>Посетителей</th><th>Просмотров</th><th>Время на странице</th><th>Дочитали</th></tr>
            </thead>
            <tbody>
              {data.top.map(r => (
                <tr key={`${r.kind}_${r.id}`}>
                  <td><Link to={`/${KIND_PATH[r.kind]}/${r.id}`}>{r.title}</Link></td>
                  <td>{r.visitors}</td><td>{r.hits}</td><td>{fmtSeconds(r.avg_seconds)}</td><td>{pct(r.read_to_end_pct)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
